/**
 * Whether THIS device has a passkey, and whether this is a new sign-in.
 * Ported from PropPay LX (lib/passkey-device.ts), keyed for SaleFish.
 *
 * Both questions are device-scoped, and both are answered locally.
 *
 * ── Why the server cannot answer the first ──
 *
 * WebAuthn gives no way to ask "does this device hold a passkey for me?". The
 * server knows whether the ACCOUNT has one (`hasPasskeys`), which is a
 * different question — and the one the old prompt asked: enrol on a laptop and
 * the phone was never offered one again. Worse, a single "not now" set
 * `passkeyPromptDismissedAt` on the account, silencing every device for ever.
 *
 * So the device records it for itself, when a passkey is enrolled here — and
 * the sign-in path should record it on a passkey SIGN-IN too (Login.tsx:
 * `markPasskeyOnThisDevice()` after a successful ceremony), since a synced
 * passkey created on a Mac is usable on the iPhone beside it without ever
 * being enrolled there.
 *
 * ── The second: a new sign-in ──
 *
 * Re-offered on each sign-in, never on a mere reload. The signal is the
 * account's `lastLoginAt`, which the server stamps on every password and SSO
 * sign-in: an offer is made once per distinct value, per device. (A passkey
 * sign-in does not stamp it — and needs no offer.)
 *
 * Storage that throws (Safari private mode) answers "has one" / "already
 * offered": a nudge we can never suppress is worse than one that never shows.
 */

const DEVICE_KEY = "salefish:passkey:on-this-device";
const OFFERED_KEY = "salefish:passkey:offered-for-sign-in";

/** This device now holds a usable passkey — enrolled here, or signed in with. */
export function markPasskeyOnThisDevice(): void {
  try {
    localStorage.setItem(DEVICE_KEY, "1");
  } catch {
    /* the offer simply appears again next sign-in */
  }
}

export function hasPasskeyOnThisDevice(): boolean {
  try {
    return localStorage.getItem(DEVICE_KEY) === "1";
  } catch {
    return true;
  }
}

/** The sign-in this device last made the offer for, if any. */
export function offeredForSignIn(): string | null {
  try {
    return localStorage.getItem(OFFERED_KEY);
  } catch {
    return "unknown";
  }
}

export function markOfferedForSignIn(signIn: string): void {
  try {
    localStorage.setItem(OFFERED_KEY, signIn);
  } catch {
    /* nothing to remember with */
  }
}

/**
 * Whether to offer enrolment. Pure, and exported for the test. Deliberately
 * does NOT consider how many passkeys the account has.
 */
export function shouldOfferPasskey(o: {
  supported: boolean;
  onThisDevice: boolean;
  /** The current sign-in's identity (lastLoginAt), or null if unknown. */
  signIn: string | null;
  /** The sign-in the offer was last made for on this device. */
  offeredFor: string | null;
}): boolean {
  if (!o.supported || o.onThisDevice) return false;
  if (!o.signIn) return false;
  return o.signIn !== o.offeredFor;
}
